import React from 'react';
import { Link } from 'react-router-dom';

/**
 * /about — Company & founder story page.
 * Uses the same chrome (RibbonBar + Navbar + Footer) as the landing page.
 */
export default function About() {
  return (
    <main id="main" data-testid="about-page">
      {/* SECTION 1 — HERO (centered page header) */}
      <section className="pt-10 pb-8 sm:pt-14 sm:pb-10 lg:pt-16 lg:pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-up">
          <p className="uppercase tracking-[0.16em] text-[11px] sm:text-xs font-semibold mb-4 sm:mb-5" style={{ color: '#5132d4' }}>
            About Crush
          </p>
          <h1 className="font-serif text-[2rem] leading-[1.1] sm:text-[2.5rem] md:text-5xl lg:text-[3rem] xl:text-[3.25rem] tracking-tight" style={{ color: '#130F28' }}>
            Marketing is the part most entrepreneurs dread. <em className="italic" style={{ color: '#5132d4' }}>We took it off your plate.</em>
          </h1>
          <p className="mt-5 sm:mt-6 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: '#3b3656' }}>
            Crush is a small team in Charleston, South Carolina, built for business owners who know they should be marketing consistently and never have the time.
          </p>
        </div>
      </section>

      {/* SECTION 2 — THE FOUNDER */}
      <section className="py-14 sm:py-20 lg:py-24" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <FounderPhoto />
          </div>
          <div className="lg:col-span-7 fade-up">
            <p className="uppercase tracking-[0.16em] text-[11px] sm:text-xs font-semibold mb-3 sm:mb-4" style={{ color: '#5132d4' }}>
              The founder
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight" style={{ color: '#130F28' }}>
              Kariesha Drayton
            </h2>
            <p className="mt-2 text-base sm:text-lg font-semibold" style={{ color: '#5132d4' }}>
              Founder &amp; CEO
            </p>
            <div className="mt-6 space-y-4 text-base sm:text-[1.05rem] leading-relaxed" style={{ color: '#1f1c33' }}>
              <p>
                I've spent my career in the parts of a business that decide whether it works. Since 2013 I've worked across marketing, business development, sales operations, and human resources in corporate America, led operations and client experience for a wedding photography and videography startup, and worked in change management as a consultant.
              </p>
              <p>
                Different industries, same pattern. The businesses that grow are the ones that stay in front of their customers. The ones that stall are usually doing everything else well.
              </p>
              <p>
                I've always gravitated toward entrepreneurs. I've judged pitch competitions, consulted for small businesses trying to get off the ground, and spent a lot of time with founders at the point where they're stretched thinnest. Now I'm one of them.
              </p>
              <p>
                I started Crush with my dad because we kept seeing the same gap. Small businesses know they should be marketing consistently. Almost none of them have the time, and hiring for it doesn't make sense at their size. So it sits undone.
              </p>
              <p className="font-semibold" style={{ color: '#130F28' }}>
                That's the part I want to make easy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHY WE BUILT CRUSH (dark band) */}
      <section className="py-16 sm:py-20 lg:py-24 relative" style={{ backgroundColor: '#0f0e1a', color: '#FAFAF9' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="uppercase tracking-[0.16em] text-[11px] sm:text-xs font-semibold mb-3 sm:mb-4" style={{ color: '#10C98F' }}>
            The reason
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight">
            Why we built <span className="italic" style={{ color: '#10C98F' }}>Crush.</span>
          </h2>
          <div className="mt-6 sm:mt-8 space-y-4 text-base sm:text-lg leading-relaxed text-white/85">
            <p>
              Every entrepreneur we've worked with has the same story. Marketing is on the list. It's been on the list for months. There's always something more urgent, and the something more urgent is usually a customer.
            </p>
            <p>
              So it gets done in bursts. A few weeks of posting, then nothing until someone notices the account has gone quiet. Meanwhile the customers you already have don't hear from you at all, and the referrals you should be getting go to someone who stayed visible.
            </p>
            <p className="font-semibold text-white">
              Crush exists so that stops being your problem.
            </p>
          </div>
          <Link
            to="/#how-it-works"
            data-testid="about-how-it-works-link"
            className="inline-flex items-center gap-1.5 mt-8 text-sm sm:text-base font-semibold group focus:outline-none focus:ring-2 focus:ring-[#10C98F] rounded"
            style={{ color: '#10C98F' }}
          >
            See how it works
            <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span>
          </Link>
        </div>
      </section>

      {/* SECTION 4 — CTA */}
      <section className="py-16 sm:py-20 lg:py-24" style={{ backgroundColor: '#FAFAF9' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight" style={{ color: '#130F28' }}>
            See what we'd write for <span className="italic" style={{ color: '#5132d4' }}>your business.</span>
          </h2>
          <div className="mt-8">
            <Link
              to="/#intake-form"
              data-testid="about-cta-trial"
              className="btn-primary inline-flex items-center justify-center rounded-full px-8 py-4 text-base sm:text-lg font-semibold"
            >
              Start your Year 1 free
            </Link>
          </div>
          <p className="mt-4 text-xs sm:text-sm" style={{ color: '#4a4566' }}>
            Free for your first year. Limited to the first 1,000 businesses to sign up. Cancel anytime.
          </p>
        </div>
      </section>
    </main>
  );
}

function FounderPhoto() {
  return (
    <div
      data-testid="about-founder-photo"
      className="relative w-full max-w-md mx-auto lg:mx-0 rounded-2xl overflow-hidden border shadow-[0_30px_80px_-40px_rgba(19,15,40,0.35)]"
      style={{ aspectRatio: '4 / 5', borderColor: '#e6e2d6' }}
    >
      <img
        src="https://customer-assets-0z36b82j.emergentagent.net/job_crush-agency-build/artifacts/iemaucu5_Kariesha%20Richardson-1195.webp"
        alt="Kariesha Drayton, Founder & CEO of Crush Marketing"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: '50% 25%' }}
        loading="lazy"
      />
    </div>
  );
}
